import { Button, Space, Table, Typography, notification, Modal, Card, Row, Col, Statistic, Input, Select } from "antd";
import { useHistory ,Link } from "react-router-dom";
import { EditOutlined, DeleteOutlined, CloseCircleOutlined ,CheckCircleOutlined, EyeOutlined, UserOutlined, CrownOutlined, SearchOutlined, TeamOutlined} from '@ant-design/icons';
import { useEffect, useState, useCallback, useRef } from "react";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import { Filter } from "../../../components";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";
import { useTableHover } from "../../../hooks/useTableHover";

const { Text, Title } = Typography

const ReferredUsers = () => {

    let history = useHistory()
    
    // Use custom table hover hook to prevent fixed column overlap
    useTableHover();

    const [filter, setFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [searchValue, setSearchValue] = useState('')
    const debounceTimer = useRef(null)

    const [type, setType] = useState(null)
    const [dataSource, setDataSource] = useState({});
    const [rawDataSource, setRawDataSource] = useState([]);
    const [additionalData, setAdditionalData] = useState({
        total_referred_users: 0,
        active_referred_users: 0,
        inactive_referred_users: 0
    });

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    console.log('rawDataSource',rawDataSource);
    const columns = [
        
        {
            title: "SNo",
            dataIndex: "id",
            key: "id",
            width: 70,
            fixed: 'left',
            render: (text, record, index) => (
                <Text style={{ fontSize: '0.85rem' }}>{(dataSource.current_page - 1) * dataSource.per_page + index + 1}</Text>
            ),
        },
        {
            title: 'Referrer Name',
            dataIndex: 'referrer_name',
            key: 'referrer_name',
            width: 180,
            ellipsis: true,
            render: (referrer_name) => <Text style={{ fontSize: '0.85rem' }}>{referrer_name ?? 'N/A'}</Text>,
        },
        {
            title: 'Referred Name',
            dataIndex: 'name',
            key: 'name',
            width: 180,
            ellipsis: true,
            render: (name) => <Text style={{ fontSize: '0.85rem' }}>{name}</Text>,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 240,
            ellipsis: true,
            render: (email) => <Text style={{ fontSize: '0.85rem' }}>{email}</Text>,
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            width: 160,
            ellipsis: true,
            render: (phone, record) => {
                const code = record?.country_code_phone || record?.country_code;
                return <Text style={{ fontSize: '0.85rem' }}>{code ? '+' + code + ' ' + (phone || '') : (phone || 'N/A')}</Text>;
            },
        },
        {
            title: 'Referral Code',
            dataIndex: 'referral_code',
            key: 'referral_code',
            width: 140,
            ellipsis: true,
            render: (referral_code) => <Text style={{ fontSize: '0.85rem' }}>{referral_code || 'N/A'}</Text>,
        },
        {
            title: 'Plan',
            dataIndex: 'stripe_id',
            key: 'stripe_id',
            width: 110,
            render: (stripe_id) => (<Text style={{ fontSize: '0.85rem' }}>{(stripe_id) ? 'Premium' : 'Free'}</Text>),
            filters: [
                { text: 'Free User', value: 'free' },
                { text: 'Paid User', value: 'paid' },
            ],
            onFilter: (value, record) => value === 'free' ? !record.stripe_id : !!record.stripe_id,
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            width: 120,
            render: (active) => (<Text style={{ fontSize: '0.85rem' }}>{(active == '1' || active == 1) ? 'Active' : 'Inactive'}</Text>),
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            onFilter: (value, record) => {
                const isActive = record.active == '1' || record.active == 1;
                return value === 'active' ? isActive : !isActive;
            },
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 110,
            fixed: 'right',
            render: (created_at) => (<Text style={{ fontSize: '0.8rem' }}>{moment(created_at).format('DD/MM/YY')}</Text>)
        },
    ];

    const buildQueryParams = (userType, name, page = null, status = null) => {
        let params = [];
        if (userType) {
            params.push(`user_type=${userType}`);
        }
        if (name) {
            params.push(`name=${name.trim()}`);
        }
        if (page !== null) {
            params.push(`page=${page}`);
        }
        if (status && status !== 'all') {
            params.push(`status=${status}`);
        }
        return params.length > 0 ? `?${params.join('&')}` : '';
    };

    // Transform API response to flatten referred_user data
    const transformData = (responseData) => {
        if (!responseData || !responseData.data) return [];
        
        return responseData.data.map(item => ({
            id: item.id,
            referrer_id: item.referrer_id,
            referred_id: item.referred_id,
            created_at: item.created_at,
            // Flatten referred_user data
            ...item.referred_user,
            // Keep original referred_user for reference
            referred_user: item.referred_user,
            referrer_name: item.referrer.name,
            referrer: item.referrer
        }));
    };

    const handleSearch = async ({ name }) => {
        const searchName = name ? name.trim() : '';
        const queryParams = buildQueryParams(type, searchName, null, statusFilter);
        const response = await ajaxService.get(`/user/referred-by${queryParams}`);

        const { success, data } = response;
        console.log('response',response);
        if (success) {
            const transformedData = transformData(data);
            setRawDataSource(transformedData);
            
            if (transformedData.length == 0 && searchName) {
                setFilter('')
                notification.open({ message: `${searchName} is not found!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
            } else {
                setFilter(searchName)
            }

            setDataSource(data)
            
            // Calculate additional data
            const activeCount = transformedData.filter(r => r.active == '1' || r.active == 1).length;
            setAdditionalData({
                total_referred_users: data.total || 0,
                active_referred_users: activeCount,
                inactive_referred_users: (data.total || 0) - activeCount
            });
        }
    }

    // Debounced search handler
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        // Only search if searchValue is provided or if filters changed
        if (searchValue !== undefined) {
            debounceTimer.current = setTimeout(() => {
                handleSearch({ name: searchValue })
            }, 500)
        }

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
        }
    }, [searchValue, type, statusFilter])

    const onClick = (newType) => {
        setType(newType)
        setFilter('')
        setSearchValue('')
        setStatusFilter('all')
        initialize(newType)
    }

    const handleChangePage = async (page) => {
        const queryParams = buildQueryParams(type, filter, page, statusFilter);
        const response = await ajaxService.get(`/user/referred-by${queryParams}`);

        if (response.success) {
            const transformedData = transformData(response.data);
            setRawDataSource(transformedData);
            setDataSource(response.data)
        }

    }

    const initialize = async (userType = type) => {
        const queryParams = buildQueryParams(userType, filter, null, statusFilter);
        const response = await ajaxService.get(`/user/referred-by${queryParams}`);

        if (response.success) {
            const transformedData = transformData(response.data);
            setRawDataSource(transformedData);
            setDataSource(response.data)
            
            // Calculate additional data
            if (response.additionalData) {
                setAdditionalData(response.additionalData)
            } else {
                const activeCount = transformedData.filter(r => r.active == '1' || r.active == 1).length;
                setAdditionalData({
                    total_referred_users: response.data.total || 0,
                    active_referred_users: activeCount,
                    inactive_referred_users: (response.data.total || 0) - activeCount
                });
            }
        }
    }

    useEffect(() => {
        initialize(type)
    }, [])

    // Listen for sidebar collapse state
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    // Calculate table scroll width using standardized utility
    // Table columns total: 70 + 180 + 240 + 160 + 140 + 110 + 120 + 110 = 1130px
    const calculateTableWidth = useCallback(() => {
        return calculateTableScrollX(sidebarCollapsed, 800, 1130, 40);
    }, [sidebarCollapsed]);

    const [tableWidth, setTableWidth] = useState(() => calculateTableScrollX(false, 800, 1130, 40));

    // Recalculate on window resize and sidebar toggle
    useEffect(() => {
        const handleResize = () => {
            setTableWidth(calculateTableWidth());
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial calculation
        
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateTableWidth]);

    return (
        <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Header Section */}
            <div className="module-header">
                <h1 className="module-title">Referral Users</h1>
                <p className="module-subtitle">Manage and monitor users referral by other users</p>
            </div>

            {/* Statistics Cards */}
            {/* <div className="stats-grid">
                <div className="stats-card">
                    <div className="stats-value">{additionalData.total_referred_users ?? 0}</div>
                    <div className="stats-label">Total Referred Users</div>
                    <TeamOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{additionalData.active_referred_users ?? 0}</div>
                    <div className="stats-label">Active Referred</div>
                    <CheckCircleOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{additionalData.inactive_referred_users ?? 0}</div>
                    <div className="stats-label">Inactive Referred</div>
                    <UserOutlined style={{ color: 'var(--text-gray)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
            </div> */}

            {/* Filters and Search */}
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-item">
                        <Input
                            placeholder="Search by name, referral user name..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onPressEnter={(e) => {
                                setSearchValue(e.target.value)
                                handleSearch({ name: e.target.value })
                            }}
                            className="input-field"
                            allowClear
                        />
                    </div>
                    <div className="filter-item">
                        <Select
                            placeholder="Filter by status"
                            style={{ width: '100%' }}
                            className="input-field"
                            value={statusFilter}
                            onChange={async (value) => {
                                setStatusFilter(value);
                                const searchName = searchValue ? searchValue.trim() : '';
                                const queryParams = buildQueryParams(type, searchName, null, value);
                                const response = await ajaxService.get(`/user/referred-by${queryParams}`);
                                if (response.success) {
                                    const transformedData = transformData(response.data);
                                    setRawDataSource(transformedData);
                                    setDataSource(response.data);
                                    
                                    const activeCount = transformedData.filter(r => r.active == '1' || r.active == 1).length;
                                    setAdditionalData({
                                        total_referred_users: response.data.total || 0,
                                        active_referred_users: activeCount,
                                        inactive_referred_users: (response.data.total || 0) - activeCount
                                    });
                                }
                            }}
                        >
                            <Select.Option value="all">All Users</Select.Option>
                            <Select.Option value="active">Active</Select.Option>
                            <Select.Option value="inactive">Inactive</Select.Option>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Referred Users Table */}
            <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <Table
                    columns={columns}
                    dataSource={rawDataSource}
                    className="data-table"
                    rowKey='id'
                    pagination={{
                        onChange: (page, size) => handleChangePage(page),
                        current: dataSource.current_page,
                        pageSize: dataSource.per_page,
                        total: dataSource.total,
                        className: "data-table-pagination",
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        size: 'small'
                    }}
                    scroll={{ x: tableWidth }}
                    size="small"
                    bordered={false}
                />
            </div>
        </div>
    )
}

export default ReferredUsers
